/* ==========================================================================
   SHIRTPLUS — SP-800-030: Warenkorbseite nach AVIS-Mengensynchronisierung
   korrekt aktualisieren
   ==========================================================================

   ZIEL
   Nach einer automatischen Mengenkorrektur durch assets/sp-cart-integrity.js
   (SP-800-027) zeigt die volle Warenkorbseite (/cart) den aktuellen
   Server-Zustand an, ohne dass ein manueller Reload noetig ist. Der Cart
   Drawer tut das bereits (er lauscht nativ auf `cart:refresh`, siehe
   assets/theme.js, CartDrawer._onCartRefresh()) - die Warenkorbseite selbst
   hat dafuer aber KEINE Bindung, weil sections/main-cart.liquid kein Custom
   Element ist (siehe ROOT CAUSE unten). Diese Datei schliesst exakt diese
   Luecke, ohne sp-cart-integrity.js, main-cart.liquid, den Cart Drawer oder
   das Premium Add-to-Cart Modal zu veraendern.

   ROOT CAUSE (live + Code-verifiziert, siehe
   SP_800_030_CART_PAGE_REFRESH_FIX_REPORT.md fuer die volle Herleitung)
   1. Normales, echtes Nutzer-Update der Warenkorbseite passiert ueber das
      native Custom Element <line-item-quantity> (Klasse LineItemQuantity,
      assets/theme.js). Dessen change-Handler ruft POST /cart/change.js auf
      und prueft danach `window.themeVariables.settings.pageType`: ist der
      Wert "cart" (Warenkorbseite), macht das Theme dort NATIV ein
      `window.location.reload()` - live per JS-Marker-Test bestaetigt (siehe
      Bericht, Test "Marker-Persistenz"): der Marker war nach einer echten
      Mengenaenderung ueber dieses Element weg, obwohl kein sichtbarer
      Seitenwechsel im Browser-Verlauf stattfand - klassisches Verhalten von
      window.location.reload().
   2. sp-cart-integrity.js macht seine Korrektur-Aufrufe an /cart/change.js
      bewusst NICHT ueber <line-item-quantity>, sondern per eigenem,
      direktem fetch() (Kommentar in der Datei: "nicht ueber die
      Theme-eigene <line-item-quantity>-Komponente"). Das ist fuer den Cart
      Drawer und die Nicht-Reload-Seiten korrekt und gewollt (kein
      ungewollter Reload waehrend z. B. eines Drawer-Kaufvorgangs) - hat auf
      der Warenkorbseite aber die Nebenwirkung, dass NIE ein Reload passiert
      und die Seite dadurch komplett auf sich selbst gestellt ist.
   3. Nach der Korrektur dispatched sp-cart-integrity.js ausschliesslich das
      bereits existierende, generische Signal `cart:refresh` (kein Payload).
      Genau dieses Event wird nativ NUR vom Cart Drawer verarbeitet
      (CartDrawer.connectedCallback() registriert einen Listener dafuer,
      main-cart.liquid registriert ueberhaupt keinen Listener) - daher
      bleibt die Warenkorbseite optisch stehen, obwohl /cart.js serverseitig
      laengst korrekt ist.
   4. Die vom Theme nativ genutzte Section-Rendering-Konvention
      (`${Shopify.routes.root}?section_id=<id>` -> HTML-Fragment der
      angefragten Section) wird exakt so bereits von
      CartDrawer._onCartRefresh() verwendet. sections/main-cart.liquid hat
      im Schema den Schluessel "class": "shopify-section--main-cart" - dadurch
      erhaelt der von Shopify automatisch erzeugte .shopify-section-Wrapper
      dauerhaft diese stabile CSS-Klasse, unabhaengig von der pro Render
      wechselnden numerischen Section-ID. Diese Datei nutzt exakt dasselbe
      native Muster (Section-Rendering-Endpoint + DOMParser + replaceChildren
      auf dem Wrapper selbst, Wrapper-Element bleibt erhalten), nur auf
      `.shopify-section--main-cart` statt `#cart-drawer` gerichtet - siehe
      Ticket Abschnitt 5, Prioritaet A.

   KEINE ENDLOSSCHLEIFE (Ticket Abschnitt 6)
   Diese Datei lauscht ausschliesslich auf `cart:refresh` und dispatched
   dieses Event an keiner Stelle selbst. Der eigene GET-Request an
   `?section_id=...` loest serverseitig weder `cart:change` noch
   `cart:refresh` aus (reines Lesen, kein /cart/change.js-Aufruf). Es gibt
   dadurch keinen Rueckkanal, der einen neuen Lauf ausloesen koennte. Ein
   isRefreshing/pendingRefresh-Zustandspaar verhindert zusaetzlich
   ueberlappende parallele Requests: trifft waehrend eines laufenden
   Refreshs ein weiteres `cart:refresh` ein, wird nach Abschluss des
   laufenden Laufs GENAU EIN weiterer Lauf nachgeholt (kein Request-Storm,
   keine mehrfachen parallelen /cart-Anfragen).
   ========================================================================== */
(function () {
  'use strict';

  if (window.__spCartPageRefreshLoaded) return;
  window.__spCartPageRefreshLoaded = true;

  var SECTION_SELECTOR = '.shopify-section--main-cart';
  var isRefreshing = false;
  var pendingRefresh = false;

  function extractSectionId(el) {
    return el.id.replace('shopify-section-', '');
  }

  function setBusyState(wrapper, busy) {
    wrapper.setAttribute('aria-busy', busy ? 'true' : 'false');

    if (busy) {
      wrapper.style.transition = 'opacity 0.15s ease';
      wrapper.style.opacity = '0.55';
      wrapper.style.pointerEvents = 'none';

      // Ticket Abschnitt 8: waehrend eines laufenden Syncs darf der Kunde
      // keine widerspruechliche Zweitaktion (weitere Mengenaenderung,
      // Entfernen) anstossen. Bestehende Bedienelemente werden kurz
      // deaktiviert; nach dem Rendering-Austausch kommen ohnehin frische,
      // voll bedienbare Elemente vom Server.
      var controls = wrapper.querySelectorAll('input, button, a');
      controls.forEach(function (el) {
        if (el.tagName === 'A') {
          el.setAttribute('aria-disabled', 'true');
          el.dataset.spWasTabindex = el.hasAttribute('tabindex') ? el.getAttribute('tabindex') : '';
          el.setAttribute('tabindex', '-1');
        } else {
          el.disabled = true;
        }
      });
    } else {
      wrapper.style.opacity = '';
      wrapper.style.pointerEvents = '';
    }
  }

  function captureFocus(wrapper) {
    var active = document.activeElement;
    if (!active || !wrapper.contains(active)) return null;

    var lineKey = active.getAttribute && active.getAttribute('data-line-key');
    return {
      lineKey: lineKey || null,
      wasRemoveLink: active.tagName === 'A' && !!active.closest('line-item-quantity')
    };
  }

  function restoreFocus(wrapper, captured) {
    if (!captured) return;

    if (captured.lineKey) {
      var again = wrapper.querySelector('[data-line-key="' + captured.lineKey + '"]');
      if (again) {
        if (captured.wasRemoveLink) {
          var link = again.closest('line-item-quantity') && again.closest('line-item-quantity').querySelector('a');
          if (link) { link.focus(); return; }
        }
        again.focus();
        return;
      }
    }

    // Ticket Abschnitt 12: Fokus darf nach dem Austausch nicht kommentarlos
    // auf <body> zurueckfallen (z. B. weil die zuvor fokussierte Zeile durch
    // die Korrektur entfernt wurde). Fallback: sichtbare Warenkorb-Ueberschrift.
    var heading = wrapper.querySelector('.cart-header h1, h1');
    if (heading) {
      if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
      heading.focus();
    }
  }

  function announce(text) {
    var region = document.getElementById('status-announcement');
    if (!region) return;
    // Bestehende, bereits im Theme vorhandene aria-live="polite"-Region
    // (layout/theme.liquid) wiederverwendet statt einer neuen, zweiten
    // Live-Region - vermeidet doppelte Screenreader-Ansagen (Ticket
    // Abschnitt 12: "keine doppelte Statusmeldung").
    region.textContent = '';
    window.setTimeout(function () { region.textContent = text; }, 50);
  }

  function refreshCartSection() {
    var wrapper = document.querySelector(SECTION_SELECTOR);
    if (!wrapper) return; // nicht auf der Warenkorbseite - keine Aktion

    if (isRefreshing) {
      pendingRefresh = true;
      return;
    }

    isRefreshing = true;
    setBusyState(wrapper, true);

    var captured = captureFocus(wrapper);
    var sectionId = extractSectionId(wrapper);
    var url = window.location.pathname +
      (window.location.search ? window.location.search + '&' : '?') +
      'section_id=' + encodeURIComponent(sectionId);

    fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (response) {
        if (!response.ok) throw new Error('cart section refresh failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var freshWrapper = parsed.querySelector(SECTION_SELECTOR);
        if (!freshWrapper) throw new Error('cart section markup not found in response');

        wrapper.replaceChildren(...freshWrapper.childNodes);
        // Custom Elements (line-item-quantity, safe-sticky, cart-discount-remove-button
        // usw.) sind bereits global via customElements.define() registriert
        // (assets/theme.js, beim initialen Seitenladen). Sobald ihre Tags
        // erneut ins Live-DOM eingehaengt werden, durchlaeuft der Browser
        // automatisch deren connectedCallback() - keine manuelle
        // Re-Initialisierung noetig.

        restoreFocus(wrapper, captured);
        announce('Warenkorb aktualisiert');
      })
      .catch(function (err) {
        // Ticket Abschnitt 5 "nicht bevorzugt": kein window.location.reload()
        // als automatischer Fallback. Stattdessen bleibt die zuletzt bekannte
        // Ansicht sichtbar (kein Datenverlust, kein ungewollter Sprung) und
        // der Fehler wird lediglich geloggt.
        if (window.console && window.console.warn) {
          window.console.warn('[sp-cart-page-refresh] Aktualisierung fehlgeschlagen:', err);
        }
      })
      .finally(function () {
        var stillWrapper = document.querySelector(SECTION_SELECTOR);
        if (stillWrapper) setBusyState(stillWrapper, false);
        isRefreshing = false;

        if (pendingRefresh) {
          pendingRefresh = false;
          refreshCartSection();
        }
      });
  }

  document.addEventListener('cart:refresh', refreshCartSection);
})();
